import { ChevronLeft, ChevronRight, Calendar, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CountryFilterButton } from "./calendar/CountryFilterButton";
import { CountryList } from "./calendar/CountryList";
import { SelectedCountries } from "./calendar/SelectedCountries";
import { cn } from "@/lib/utils";

interface CalendarHeaderProps {
  currentMonth: string;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  selectedCountries: string[];
  onCountrySelect: (countryCode: string) => void;
  onClearFilters: () => void;
}

export const CalendarHeader = ({
  currentMonth,
  onPrevMonth,
  onNextMonth,
  selectedCountries,
  onCountrySelect,
  onClearFilters,
}: CalendarHeaderProps) => {
  console.log("CalendarHeader: Selected countries:", selectedCountries);
  
  return (
    <div className="flex flex-col overflow-hidden">
      {/* Enhanced premium header for 2025 design */}
      <div className={cn(
        "relative flex items-center justify-between p-4 md:p-5 lg:p-6",
        "bg-gradient-to-r from-slate-900/80 via-slate-800/70 to-slate-900/80",
        "backdrop-blur-md border-b border-slate-600/30",
        "shadow-lg shadow-black/20"
      )}>
        
        {/* Ambient glow effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/3 to-blue-500/5 pointer-events-none" />
        
        <div className="relative flex items-center gap-4 md:gap-6">
          {/* Enhanced navigation controls with premium styling */}
          <div className="flex items-center bg-gradient-to-r from-slate-800/60 to-slate-700/60 rounded-xl border border-slate-600/40 overflow-hidden backdrop-blur-sm shadow-md">
            <Button
              variant="ghost"
              size="icon"
              onClick={onPrevMonth}
              className={cn(
                "h-9 w-9 md:h-11 md:w-11 rounded-none border-r border-slate-600/30",
                "text-slate-300 hover:text-white hover:bg-gradient-to-r hover:from-slate-600/60 hover:to-slate-700/60",
                "transition-all duration-300 group"
              )}
            >
              <ChevronLeft className="h-4 w-4 md:h-5 md:w-5 group-hover:scale-110 transition-transform duration-200" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onNextMonth}
              className={cn(
                "h-9 w-9 md:h-11 md:w-11 rounded-none",
                "text-slate-300 hover:text-white hover:bg-gradient-to-r hover:from-slate-600/60 hover:to-slate-700/60",
                "transition-all duration-300 group"
              )}
            >
              <ChevronRight className="h-4 w-4 md:h-5 md:w-5 group-hover:scale-110 transition-transform duration-200" />
            </Button>
          </div>
          
          {/* Enhanced month display with premium typography */}
          <div className="flex items-center gap-3 md:gap-4">
            <div className="relative flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-400/30 backdrop-blur-sm shadow-lg">
              <Calendar className="h-5 w-5 md:h-6 md:w-6 text-blue-400" />
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500/10 to-transparent opacity-50" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl md:text-2xl lg:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-purple-100 tracking-tight">
                {currentMonth}
              </span>
              <span className="text-xs md:text-sm text-slate-400 font-medium hidden sm:block bg-gradient-to-r from-slate-400 to-slate-500 bg-clip-text text-transparent">
                Economic Calendar • 2025
              </span>
            </div>
          </div>
        </div>
        
        {/* Enhanced filter controls with premium styling */}
        <div className="relative">
          <Popover>
            <PopoverTrigger asChild>
              <div>
                <Button
                  variant="outline"
                  className={cn(
                    "relative h-9 md:h-11 px-4 md:px-5",
                    "bg-gradient-to-r from-slate-800/60 to-slate-700/60 border-slate-500/50 text-slate-200",
                    "hover:from-slate-700/70 hover:to-slate-600/70 hover:border-slate-400/60 hover:text-white",
                    "backdrop-blur-sm shadow-md transition-all duration-300",
                    "flex items-center gap-2 md:gap-3 group",
                    selectedCountries.length > 0 && [
                      "bg-gradient-to-r from-blue-500/30 to-purple-500/30 border-blue-400/60 text-blue-200",
                      "hover:from-blue-500/40 hover:to-purple-500/40 hover:border-blue-300/70",
                      "shadow-lg shadow-blue-500/20"
                    ]
                  )}
                >
                  <Filter className="h-4 w-4 md:h-4 md:w-4 group-hover:scale-110 transition-transform duration-200" />
                  <span className="hidden sm:inline font-medium text-sm">
                    Filter by Countries
                  </span>
                  {selectedCountries.length > 0 && (
                    <div className="flex items-center justify-center w-5 h-5 md:w-6 md:h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white text-xs font-bold shadow-md">
                      {selectedCountries.length}
                    </div>
                  )}
                </Button>
              </div>
            </PopoverTrigger>
            <PopoverContent 
              className={cn(
                "w-[300px] md:w-[360px] p-0",
                "bg-slate-900/95 backdrop-blur-xl border border-slate-600/50",
                "shadow-2xl shadow-black/40 rounded-xl",
                "animate-in fade-in-0 zoom-in-95 duration-200"
              )}
              align="end"
              sideOffset={12}
            >
              <CountryList 
                selectedCountries={selectedCountries}
                onCountrySelect={onCountrySelect}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
      
      {/* Enhanced selected countries section with premium styling */}
      {selectedCountries.length > 0 && (
        <div className={cn(
          "px-4 md:px-5 lg:px-6 py-3 md:py-4",
          "bg-gradient-to-r from-slate-800/50 via-slate-800/30 to-slate-800/50",
          "backdrop-blur-sm border-b border-slate-600/20",
          "shadow-inner shadow-slate-900/20"
        )}>
          <SelectedCountries 
            selectedCountries={selectedCountries}
            onCountrySelect={onCountrySelect}
            onClearFilters={onClearFilters}
          />
        </div>
      )}
    </div>
  );
};